// Application Configuration
export const APP_NAME = 'CR Automation'
export const APP_VERSION = '1.0.0'
export const JOB_ENGINE_NAME = 'Job Engine'

// Auth
export const AUTH_TOKEN_KEY = 'cr_auth_token'
export const AUTH_USER_KEY = 'cr_auth_user'
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || ''

// Inverter brand constants
export const INVERTER_BRANDS = {
  SOLAREDGE: 'SOLAREDGE',
  TESLA: 'TESLA',
  ENPHASE: 'ENPHASE',
  QCELLS: 'QCELLS'
} as const;

// Roof Configuration
export const REROOF_ENABLED_ROOF_TYPES = [
  'Comp Shingle',
  'Metal Trapezoidal',
  'Metal Seams',
  'Metal Corrugated',
  'Rolled Comp'
] as const

// Routes
export const ROUTES = {
  HOME: '/',
  CREATE_CR: '/create',
  EDIT_CR_WORKFLOW: '/create/:id',
  REVIEW_CR: '/review',
  EDIT_CR: '/review/:id',
  SETTINGS: '/settings',
  IMPORT: '/import',
  CLIENTS: '/clients',
  CLIENT_DETAIL: '/clients/:id',
  USERS: '/users',
  ANALYTICS: '/analytics'
} as const

// Page Titles
export const PAGE_TITLES = {
  HOME: 'Construction Review Automation Platform',
  CREATE_CR: 'Create Construction Review',
  REVIEW_CR: 'Review Construction Reviews',
  EDIT_CR: 'Edit Construction Review',
  SETTINGS: 'Settings',
  MODULE_BRANDS: 'Module Brands',
  SOLAR_MODULES: 'Solar Modules'
} as const

// UI Configuration
export const UI_CONFIG = {
  typography: {
    hero: { size: '4xl' },
    subtitle: { size: 'xl' },
    heading: { size: '2xl' },
    body: { size: 'sm' }
  },
  animation: {
    duration: {
      fast: '200',
      normal: '300',
      slow: '500'
    }
  },
  pagination: {
    itemsPerPage: 10,
    maxVisiblePages: 5
  },
  table: {
    rowHeight: 'h-12',
    columnWidths: {
      taskName: 'w-[50%]',
      taskId: 'w-[25%]',
      items: 'w-[10%]',
      actions: 'w-[15%]'
    }
  },
  editableTable: {
    columnWidths: {
      key: 'w-[25%]',
      value: 'w-[63%]',
      actions: 'w-[12%]'
    },
    maxInverters: 3
  },
  toast: {
    duration: 5000,
    position: 'bottom-right' as const
  }
} as const

// Messages
export const MESSAGES = {
  LOADING: 'Loading...',
  NO_DATA: 'No data available',
  ERROR: 'An error occurred. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SUCCESS: {
    CR_CREATED: 'Construction Review created successfully!',
    CR_UPDATED: 'Construction Review updated successfully!',
    CR_DELETED: 'Construction Review deleted successfully!',
    CR_COPIED: 'Construction Review copied to clipboard!',
    DATA_SAVED: 'Data saved successfully!'
  },
  ERROR_MESSAGES: {
    CR_CREATE_FAILED: 'Failed to create Construction Review',
    CR_UPDATE_FAILED: 'Failed to update Construction Review',
    CR_DELETE_FAILED: 'Failed to delete Construction Review',
    CR_LOAD_FAILED: 'Failed to load Construction Review',
    CR_COPY_FAILED: 'Failed to copy to clipboard',
    VALIDATION_ERROR: 'Please fill out all required fields'
  },
  CONFIRMATION: {
    DELETE_CR:
      'Are you sure you want to delete this Construction Review? This action cannot be undone.',
    SAVE_CR:
      'This will guide to a new CR generation from scratch and all changes will be reset. Do you want to continue?'
  }
} as const

// CR auto-save feedback (fire-and-forget upsert of the CR record)
export const CR_AUTO_SAVE = {
  SUCCESS_MESSAGE: 'Construction review auto-saved',
  BOM_SAVED_MESSAGE: 'BOM saved',
  ERROR_MESSAGE: 'CR Auto-save failed',
  SUCCESS_TOAST_DURATION_MS: 1500,
  ERROR_TOAST_DURATION_MS: 2000
} as const

// Toast Configuration
export const TOAST_CONFIG = {
  position: UI_CONFIG.toast.position,
  toastOptions: {
    duration: UI_CONFIG.toast.duration,
    style: {
      background: '#ffffff',
      color: '#0f172a',
      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
      borderRadius: '8px',
      fontSize: '14px'
    },
    success: {
      style: {
        background: '#0f766e',
        color: '#ffffff',
        border: '1px solid #0d9488'
      }
    },
    error: {
      style: {
        background: '#EF4444',
        color: '#fff',
        border: '1px solid #DC2626'
      }
    }
  }
} as const

// Warning toast helper (since react-hot-toast doesn't have a built-in warning method)
export const TOAST_WARNING_CONFIG = {
  style: {
    background: '#f59e0b',
    color: '#ffffff',
    border: '1px solid #d97706',
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
    borderRadius: '8px',
    fontSize: '14px'
  },
  duration: UI_CONFIG.toast.duration
} as const

// POI Configuration
export const POI_CONFIG = {
  LINE_SIDE_TAP: 'LINE SIDE TAP',
  LOAD_SIDE_TAP: 'LOAD SIDE TAP',
  LOCATION_ATS: 'ATS',
  BREAKER_TIE_IN_MSP: 'BREAKER TIE-IN MSP',
  BREAKER_TIE_IN_SUB: 'BREAKER TIE-IN SUB'
} as const

// AEC Configuration
export const AEC_CONFIG = {
  TYPES: {
    MAIN_BREAKER_DERATE: 'MAIN BREAKER DERATE',
    SUBPANEL_INSERT: 'SUBPANEL INSERT'
  }
} as const

// Subpanel Configuration
export const SUBPANEL_CONFIG = {
  FEEDING_METHODS: {
    BREAKER_IN_MSP: 'Breaker in MSP'
  }
} as const

// Utility Configuration
export const UTILITY_CONFIG = {
  SERVICE_FEED_SOURCE_OPTIONS: [
    { id: 'overhead', label: 'OVERHEAD' },
    { id: 'underground', label: 'UNDERGROUND' },
    { id: 'on_pole', label: 'ON POLE' }
  ],
  DEFAULT_DISTANCE_TEXT: "VISIBLE, LOCKABLE, LABELED KNIFE BLADE AC DISCONNECT LOCATED WITHIN 10' OF UTILITY METER"
}

// Construction Review Attribute Order
export const CR_ATTRIBUTE_ORDER = [
  'CUSTOMER',
  'AHJ',
  'UTILITY',
  'UM#',
  'APN',
  'NEC',
  'IBC',
  'IRC',
  'IFC',
  // System 1
  'MODULES',
  'MODULES (S1)',
  'INVERTERS',
  'INVERTERS (S1)',
  'MICROINVERTERS',
  'MICROINVERTERS (S1)',
  'OPTIMIZERS',
  'OPTIMIZERS (S1)',
  'RSD',
  'RSD (S1)',
  'COMBINER',
  'COMBINER (S1)',
  'BATTERY',
  'BATTERY (S1)',
  'STRINGS',
  'STRINGS (S1)',
  // System 2
  'MODULES (S2)',
  'INVERTERS (S2)',
  'MICROINVERTERS (S2)',
  'OPTIMIZERS (S2)',
  'RSD (S2)',
  'COMBINER (S2)',
  'BATTERY (S2)',
  'STRINGS (S2)',
  // System 3
  'MODULES (S3)',
  'INVERTERS (S3)',
  'MICROINVERTERS (S3)',
  'OPTIMIZERS (S3)',
  'RSD (S3)',
  'COMBINER (S3)',
  'BATTERY (S3)',
  'STRINGS (S3)',
  // Production (shared)
  'PRODUCTION',
  'MSP',
  'POI',
  'ATS',
  'MBE',
  'BLP',
  'SUBPANEL',
  'UTILITY CONFIG',
  'PRODUCTION METER',
  'DISCO 1',
  'DISCO 2',
  'DISCO 3',
  'DISCO 4',
  'DISCO 5',
  'MOUNTING SYSTEM',
  'ROOF-MOUNT CONDUIT',
  'BOS CONDUIT',
  'TRENCH CONDUIT',
  'STRUCTURE (1) NAME',
  'FRAMING (1)',
  'ROOF (1)',
  'REROOF (1)',
  'BUILDING HEIGHT (1)',
  'ATTACHMENT (1) ONE',
  'RAIL (1) ONE',
  'ATTACHMENT (1) TWO',
  'RAIL (1) TWO',
  'STRUCTURE (2) NAME',
  'FRAMING (2)',
  'ROOF (2)',
  'REROOF (2)',
  'BUILDING HEIGHT (2)',
  'ATTACHMENT (2) ONE',
  'RAIL (2) ONE',
  'ATTACHMENT (2) TWO',
  'RAIL (2) TWO',
  'STRUCTURE (3) NAME',
  'FRAMING (3)',
  'ROOF (3)',
  'REROOF (3)',
  'BUILDING HEIGHT (3)',
  'ATTACHMENT (3) ONE',
  'RAIL (3) ONE',
  'ATTACHMENT (3) TWO',
  'RAIL (3) TWO',
  'BOS LOCATION',
  'AURORA',
  'GDRIVE LINK',
  'NOTION (AHJ)',
  'NOTION (UX)',
  'NOTION (CX)',
  'INSTALL NOTES',
  'POI NOTES',
  'DESIGN NOTES',
  'MISCELLANEOUS NOTES',
  'LGCY LIS#'
] as const

// Landing Page Configuration
export const LANDING_MIN_ROLE = {
  EVERYONE: 'everyone',
  ADMIN: 'admin',
  SUPERADMIN: 'superadmin'
} as const

export type LandingMinRole = typeof LANDING_MIN_ROLE[keyof typeof LANDING_MIN_ROLE]

export const LANDING_CONFIG = {
  hero: {
    eyebrow: 'CR Workflow Control',
    title: 'Construction Review',
    subtitle: 'Automation Platform',
    description:
      'Configure, review, and ship construction reviews — all in one workspace.'
  },
  primaryCards: [
    {
      id: 'create',
      title: 'Create New CR',
      description:
        'Start a new construction review and generate a complete, validated report.',
      icon: 'plus',
      route: ROUTES.CREATE_CR,
      iconColor: 'text-orange-600',
      accent: 'orange',
      cta: 'Start a new review'
    },
    {
      id: 'review',
      title: 'Review Existing CRs',
      description:
        'Browse, edit, and manage every CR your team has submitted.',
      icon: 'document',
      route: ROUTES.REVIEW_CR,
      iconColor: 'text-sky-600',
      accent: 'sky',
      cta: 'Open review queue'
    },
    {
      id: 'settings',
      title: 'Settings',
      description:
        'Configure system preferences for the workspace.',
      icon: 'config',
      route: ROUTES.SETTINGS,
      iconColor: 'text-rose-600',
      accent: 'rose',
      cta: 'Open settings'
    }
  ],
  adminTools: [
    {
      id: 'analytics',
      title: 'Analytics',
      description: 'Team performance and CR throughput',
      icon: 'analytics',
      route: ROUTES.ANALYTICS,
      accent: 'indigo',
      minRole: LANDING_MIN_ROLE.ADMIN
    },
    {
      id: 'clients',
      title: 'Clients',
      description: 'Catalogs, lenders, and packages',
      icon: 'clients',
      route: ROUTES.CLIENTS,
      accent: 'emerald',
      minRole: LANDING_MIN_ROLE.ADMIN
    },
    {
      id: 'users',
      title: 'Users',
      description: 'Team members and access levels',
      icon: 'users',
      route: ROUTES.USERS,
      accent: 'violet',
      minRole: LANDING_MIN_ROLE.SUPERADMIN
    },
  ]
} as const

export const LANDING_ACCENT_STYLES: Record<
  string,
  {
    iconColor: string
    ring: string
    text: string
    chipBg: string
    chipBorder: string
    hoverGlow: string
  }
> = {
  orange: {
    iconColor: 'text-orange-600',
    ring: 'group-hover:ring-orange-200',
    text: 'group-hover:text-orange-700',
    chipBg: 'bg-gradient-to-br from-orange-50 to-orange-100/70',
    chipBorder: 'border-orange-200/70',
    hoverGlow: 'via-orange-400/70'
  },
  sky: {
    iconColor: 'text-sky-600',
    ring: 'group-hover:ring-sky-200',
    text: 'group-hover:text-sky-700',
    chipBg: 'bg-gradient-to-br from-sky-50 to-sky-100/70',
    chipBorder: 'border-sky-200/70',
    hoverGlow: 'via-sky-400/70'
  },
  indigo: {
    iconColor: 'text-indigo-600',
    ring: 'group-hover:ring-indigo-200',
    text: 'group-hover:text-indigo-700',
    chipBg: 'bg-gradient-to-br from-indigo-50 to-indigo-100/70',
    chipBorder: 'border-indigo-200/70',
    hoverGlow: 'via-indigo-400/70'
  },
  emerald: {
    iconColor: 'text-emerald-600',
    ring: 'group-hover:ring-emerald-200',
    text: 'group-hover:text-emerald-700',
    chipBg: 'bg-gradient-to-br from-emerald-50 to-emerald-100/70',
    chipBorder: 'border-emerald-200/70',
    hoverGlow: 'via-emerald-400/70'
  },
  violet: {
    iconColor: 'text-violet-600',
    ring: 'group-hover:ring-violet-200',
    text: 'group-hover:text-violet-700',
    chipBg: 'bg-gradient-to-br from-violet-50 to-violet-100/70',
    chipBorder: 'border-violet-200/70',
    hoverGlow: 'via-violet-400/70'
  },
  rose: {
    iconColor: 'text-rose-600',
    ring: 'group-hover:ring-rose-200',
    text: 'group-hover:text-rose-700',
    chipBg: 'bg-gradient-to-br from-rose-50 to-rose-100/70',
    chipBorder: 'border-rose-200/70',
    hoverGlow: 'via-rose-400/70'
  }
}

// Panel Rows Configuration
export const PANEL_ROWS_MAX = 31

export const PANEL_ORIENTATION_OPTIONS = [
  { value: 'LANDSCAPE', label: 'Landscape' },
  { value: 'PORTRAIT', label: 'Portrait' }
] as const

// Settings Configuration
export const SETTING_TYPES = {
  SAFETY_FACTOR: 'safety_factor',
  LGCY_BOM_OFFSET: 'lgcy_bom_offset',
  CLIENT_LOGOS: 'client_logos'
} as const

export const SETTING_DEFAULTS = {
  [SETTING_TYPES.SAFETY_FACTOR]: 2,
  [SETTING_TYPES.LGCY_BOM_OFFSET]: 5,
  [SETTING_TYPES.CLIENT_LOGOS]: {} as Record<string, string>
} as const

export const SETTING_CONSTRAINTS = {
  [SETTING_TYPES.SAFETY_FACTOR]: { min: 1 },
  [SETTING_TYPES.LGCY_BOM_OFFSET]: { min: 0, max: 100 }
} as const

export const SETTING_LABELS: Record<string, { label: string; description: string }> = {
  [SETTING_TYPES.SAFETY_FACTOR]: {
    label: 'Safety Factor',
    description: 'Used for BOM calculation for safety margin'
  },
  [SETTING_TYPES.LGCY_BOM_OFFSET]: {
    label: 'LGCY BOM Offset (%)',
    description: 'Percentage offset applied to LGCY BOM calculations instead of safety factor'
  },
  [SETTING_TYPES.CLIENT_LOGOS]: {
    label: 'Client Logos',
    description: 'Mapping of client names to logo identifiers'
  }
}

// Equipment Step Defaults
export const EQUIPMENT_DEFAULTS = {
  RSD_TYPE: 'MCI-2',
} as const

// Mounting Step Defaults
export const MOUNTING_DEFAULTS = {
  MOUNT_SYSTEM: 'ROOF',
  ROOF_CONDUIT_SIZE: '3/4"',
  ROOF_CONDUIT_TYPE: 'EMT CONDUIT RUN',
} as const

// Unit Conversion
export const MM_TO_INCH = 25.4

// Time Conversion
export const TIME_CONVERSIONS = {
  MS_PER_SECOND: 1000,
  SECONDS_PER_MINUTE: 60,
  MINUTES_PER_HOUR: 60,
  HOURS_PER_DAY: 24
} as const

export const MS_PER_DAY =
  TIME_CONVERSIONS.HOURS_PER_DAY *
  TIME_CONVERSIONS.MINUTES_PER_HOUR *
  TIME_CONVERSIONS.SECONDS_PER_MINUTE *
  TIME_CONVERSIONS.MS_PER_SECOND

// Shared Page Layout Classes
export const PAGE_LAYOUT = {
  WIDE_CONTAINER: 'mx-[9%] my-4',
  WIDE_CONTAINER_NO_VERTICAL: 'mx-[9%]',
  PAGE_WRAPPER: 'min-h-full bg-slate-50 pt-8 pb-10'
} as const
